import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Table,
  Tag,
  Image,
  Modal,
  Pagination,
  Typography,
  Space,
  Spin,
} from "antd";
import {
  CreditCardOutlined,
  UserOutlined,
  AppstoreOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useSuccessErrorModal from "../../hooks/successErrorModal/useSuccessErrorModal";
import EndpointFunctions from "../../utils/Functions/endpointFunctions";
import Endpoints from "../../api/endpoints";
import routes from "../../utils/routes";
import UtilFunctions from "../../utils/Functions/utilFunctions";

const { Link, Text } = Typography;

const formatDateTime = (iso) =>
  iso ? dayjs(iso).format("DD.MM.YYYY HH:mm") : "-";

const formatPhone = (countryCode, phoneNumber) => {
  if (!phoneNumber) return "-";
  return countryCode ? `+${countryCode} ${phoneNumber}` : phoneNumber;
};

const ColorDot = ({ color, size = 24 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      backgroundColor: UtilFunctions.convertDecimalToColor(color),
      border: "1px solid #ddd",
      display: "inline-block",
      verticalAlign: "middle",
    }}
  />
);

const BarcodeTag = ({ format }) =>
  format ? <Tag color="blue">{format}</Tag> : <Tag>-</Tag>;

const OtherCardUsage = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { showModal, closeModal, ModalComponent } = useSuccessErrorModal();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [companyUsage, setCompanyUsage] = useState([]);
  const [companyUsageLoading, setCompanyUsageLoading] = useState(false);

  const [customCards, setCustomCards] = useState([]);
  const [customCardsLoading, setCustomCardsLoading] = useState(false);
  const [customFilter, setCustomFilter] = useState({
    page: 0,
    size: 20,
    total: 0,
    pageCount: 0,
  });

  // Drill-down modal
  const [drillCompany, setDrillCompany] = useState(null); // { companyId, companyName, logoUrl, color }
  const [drillClients, setDrillClients] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillFilter, setDrillFilter] = useState({
    page: 0,
    size: 20,
    total: 0,
    pageCount: 0,
  });

  useEffect(() => {
    fetchStats();
    fetchCompanyUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchCustomCards(controller);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFilter.page, customFilter.size]);

  useEffect(() => {
    if (!drillCompany) return;
    const controller = new AbortController();
    fetchDrillClients(controller);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drillCompany, drillFilter.page, drillFilter.size]);

  const fetchStats = async () => {
    setStatsLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getOtherCardStats,
      axiosPrivate,
    });
    if (result.success) {
      setStats(result.data);
    } else {
      showModal(result.errorTitle, result.errorMessage, closeModal);
    }
    setStatsLoading(false);
  };

  const fetchCompanyUsage = async () => {
    setCompanyUsageLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getOtherCardCompanyUsage,
      axiosPrivate,
    });
    if (result.success) {
      setCompanyUsage(
        (result.data ?? []).map((item) => ({ ...item, key: item.companyId }))
      );
    } else {
      showModal(result.errorTitle, result.errorMessage, closeModal);
    }
    setCompanyUsageLoading(false);
  };

  const fetchCustomCards = async (controller) => {
    setCustomCardsLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getOtherCardCustomCards,
      axiosPrivate,
      httpParams: { page: customFilter.page, size: customFilter.size },
      controller,
    });
    if (controller.signal.aborted) return;
    if (result.success) {
      const items = result?.data?.data ?? [];
      setCustomCards(items.map((item) => ({ ...item, key: item.cardId })));
      setCustomFilter((prev) => ({
        ...prev,
        total: result?.data?.total ?? 0,
        size: result?.data?.pageSize ?? prev.size,
        pageCount: result?.data?.pageCount ?? 0,
      }));
    } else {
      showModal(result.errorTitle, result.errorMessage, closeModal);
    }
    setCustomCardsLoading(false);
  };

  const fetchDrillClients = async (controller) => {
    setDrillLoading(true);
    const result = await EndpointFunctions.fetchData({
      endpointURL: Endpoints.getOtherCardClientsByCompany,
      axiosPrivate,
      httpParams: {
        companyId: drillCompany.companyId,
        page: drillFilter.page,
        size: drillFilter.size,
      },
      controller,
    });
    if (controller.signal.aborted) return;
    if (result.success) {
      const items = result?.data?.data ?? [];
      setDrillClients(items.map((item) => ({ ...item, key: item.cardId })));
      setDrillFilter((prev) => ({
        ...prev,
        total: result?.data?.total ?? 0,
        size: result?.data?.pageSize ?? prev.size,
        pageCount: result?.data?.pageCount ?? 0,
      }));
    } else {
      showModal(result.errorTitle, result.errorMessage, closeModal);
    }
    setDrillLoading(false);
  };

  const openDrillDown = (record) => {
    setDrillFilter({ page: 0, size: 20, total: 0, pageCount: 0 });
    setDrillClients([]);
    setDrillCompany({
      companyId: record.companyId,
      companyName: record.companyName,
      logoUrl: record.logoUrl,
      color: record.color,
    });
  };

  const closeDrillDown = () => {
    setDrillCompany(null);
    setDrillClients([]);
  };

  const companyColumns = [
    {
      title: "Logo",
      dataIndex: "logoUrl",
      key: "logoUrl",
      width: 80,
      render: (value, record) =>
        value ? (
          <Image
            src={value}
            alt={record.companyName}
            width={40}
            height={40}
            style={{ objectFit: "contain" }}
            preview={false}
          />
        ) : (
          <ColorDot color={record.color} />
        ),
    },
    {
      title: "Marka",
      dataIndex: "companyName",
      key: "companyName",
      render: (value, record) => (
        <Space>
          <span>{value}</span>
          {record.isCommonlyUsed && (
            <Tag color="green">Sık Kullanılan</Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Kart Sayısı",
      dataIndex: "cardCount",
      key: "cardCount",
      width: 140,
      align: "right",
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: "Müşteri Sayısı",
      dataIndex: "clientCount",
      key: "clientCount",
      width: 140,
      align: "right",
    },
  ];

  const customColumns = [
    {
      title: "Marka",
      key: "company",
      render: (_, record) => (
        <Space>
          <ColorDot color={record.customCompanyColor} size={20} />
          <span>{record.customCompanyName || "-"}</span>
        </Space>
      ),
    },
    {
      title: "Müşteri",
      key: "client",
      render: (_, record) => (
        <Link
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.clientDetails}/${record.clientId}`);
          }}>
          {record.clientName || "-"}
        </Link>
      ),
    },
    {
      title: "Telefon",
      key: "phone",
      render: (_, record) =>
        formatPhone(record.countryCode, record.phoneNumber),
    },
    {
      title: "Takma Ad",
      dataIndex: "nickname",
      key: "nickname",
      render: (value) => value || <Text type="secondary">-</Text>,
    },
    {
      title: "Kart No",
      dataIndex: "cardNumber",
      key: "cardNumber",
      render: (value) => value || "-",
    },
    {
      title: "Barkod",
      dataIndex: "barcodeFormat",
      key: "barcodeFormat",
      width: 140,
      render: (value) => <BarcodeTag format={value} />,
    },
    {
      title: "Eklenme Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: formatDateTime,
    },
  ];

  const drillColumns = [
    {
      title: "Müşteri",
      key: "client",
      render: (_, record) => (
        <Link
          onClick={() => {
            closeDrillDown();
            navigate(`${routes.clientDetails}/${record.clientId}`);
          }}>
          {record.clientName || "-"}
        </Link>
      ),
    },
    {
      title: "Telefon",
      key: "phone",
      render: (_, record) =>
        formatPhone(record.countryCode, record.phoneNumber),
    },
    {
      title: "Takma Ad",
      dataIndex: "nickname",
      key: "nickname",
      render: (value) => value || <Text type="secondary">-</Text>,
    },
    {
      title: "Kart No",
      dataIndex: "cardNumber",
      key: "cardNumber",
      render: (value) => value || "-",
    },
    {
      title: "Barkod",
      dataIndex: "barcodeFormat",
      key: "barcodeFormat",
      width: 140,
      render: (value) => <BarcodeTag format={value} />,
    },
    {
      title: "Eklenme Tarihi",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: formatDateTime,
    },
  ];

  const distributionColumns = [
    {
      title: "Kart Sayısı (kişi başına)",
      dataIndex: "cardCount",
      key: "cardCount",
      align: "center",
      width: 200,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: "Müşteri Sayısı",
      dataIndex: "clientCount",
      key: "clientCount",
      align: "right",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Spin spinning={statsLoading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Toplam Kart"
                value={stats?.totalCards ?? 0}
                prefix={<CreditCardOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Kart Sahibi Müşteri"
                value={stats?.totalClientsWithCards ?? 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Hazır Marka Kart"
                value={stats?.predefinedCards ?? 0}
                prefix={<AppstoreOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Özel Kart"
                value={stats?.customCards ?? 0}
                prefix={<EditOutlined />}
              />
            </Card>
          </Col>
        </Row>
      </Spin>

      <div style={{ height: 24 }} />

      <Card
        type="inner"
        title="Müşteri Başına Kart Dağılımı"
        size="small"
        styles={{ body: { padding: 0 } }}>
        <Table
          size="small"
          pagination={false}
          dataSource={(stats?.cardsPerClientDistribution ?? []).map(
            (row, idx) => ({ ...row, key: idx })
          )}
          columns={distributionColumns}
          locale={{ emptyText: "Veri yok" }}
        />
      </Card>

      <div style={{ height: 24 }} />

      <Tabs
        defaultActiveKey="predefined"
        items={[
          {
            key: "predefined",
            label: "Hazır Markalar",
            children: (
              <Table
                sticky
                pagination={false}
                dataSource={companyUsage}
                columns={companyColumns}
                loading={companyUsageLoading}
                scroll={{ x: "max-content" }}
                onRow={(record) => ({
                  onClick: () => openDrillDown(record),
                  style: { cursor: "pointer" },
                })}
              />
            ),
          },
          {
            key: "custom",
            label: "Özel Kartlar",
            children: (
              <>
                <Table
                  sticky
                  pagination={false}
                  dataSource={customCards}
                  columns={customColumns}
                  loading={customCardsLoading}
                  scroll={{ x: "max-content" }}
                />
                <div style={{ height: 16 }} />
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Pagination
                    total={customFilter.total}
                    pageSize={customFilter.size}
                    current={customFilter.page + 1}
                    showSizeChanger
                    pageSizeOptions={[20, 50, 100]}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} / toplam ${total} kart`
                    }
                    onChange={(page, pageSize) =>
                      setCustomFilter((prev) => ({
                        ...prev,
                        page: page - 1,
                        size: pageSize,
                      }))
                    }
                  />
                </div>
              </>
            ),
          },
        ]}
      />

      <Modal
        open={!!drillCompany}
        onCancel={closeDrillDown}
        footer={null}
        width={900}
        title={
          drillCompany && (
            <Space>
              {drillCompany.logoUrl ? (
                <Image
                  src={drillCompany.logoUrl}
                  alt={drillCompany.companyName}
                  width={28}
                  height={28}
                  preview={false}
                  style={{ objectFit: "contain" }}
                />
              ) : (
                <ColorDot color={drillCompany.color} size={20} />
              )}
              <span>{drillCompany.companyName} — Kart Kullanan Müşteriler</span>
            </Space>
          )
        }>
        <Table
          size="middle"
          pagination={false}
          dataSource={drillClients}
          columns={drillColumns}
          loading={drillLoading}
          scroll={{ x: "max-content" }}
        />
        <div style={{ height: 16 }} />
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            total={drillFilter.total}
            pageSize={drillFilter.size}
            current={drillFilter.page + 1}
            showSizeChanger
            pageSizeOptions={[20, 50, 100]}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} / toplam ${total} müşteri`
            }
            onChange={(page, pageSize) =>
              setDrillFilter((prev) => ({
                ...prev,
                page: page - 1,
                size: pageSize,
              }))
            }
          />
        </div>
      </Modal>

      <ModalComponent />
    </div>
  );
};

export default OtherCardUsage;
